<template>
  <div class="delivery-list-container">
    <div class="header">
      <div class="title">送货列表</div>
      <div class="stats-bar">
        <div class="stat-item">
          <div class="stat-value">{{ totalAmount }}元</div>
          <div class="stat-label">待送货总额</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ total }}</div>
          <div class="stat-label">待送货数量</div>
        </div>
      </div>
      <div class="search-box">
        <van-search
          v-model="searchValue"
          placeholder="输入收件人手机号进行查询"
          shape="round"
          @search="onSearch"
        />
      </div>
    </div>

    <div class="delivery-list" v-if="deliveries.length > 0">
      <div 
        v-for="(delivery, index) in deliveries" 
        :key="delivery.id || index"
        class="delivery-item"
        @click="goToDeliveryDetail(delivery.id)"
      >
        <div class="delivery-header">
          <div class="order-info">
            <div class="order-type">
              <van-tag :type="getDictTagType(GOODS_NAME, delivery.goodsName)" size="medium" round>
                {{ getDictLabel(GOODS_NAME, delivery.goodsName) || delivery.goodsName }}
              </van-tag>
            </div>
            <div class="order-no">订单号：{{ delivery.code || '暂无' }}</div>
          </div>
          <div class="delivery-status">
            <van-tag :type="getDictTagType(BILL_STATUS, delivery.status)" size="medium" plain>
              {{ getDictLabel(BILL_STATUS, delivery.status) }}
            </van-tag>
          </div>
        </div>

        <div class="delivery-info">
          <div class="info-row">
            <span class="info-label">收货人:</span>
            <span class="info-value">{{ delivery.receiverName || '暂无' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">联系电话:</span>
            <span class="info-value">{{ delivery.phone || '暂无' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">收货地址:</span>
            <span class="info-value">{{ delivery.receiverAddress || '暂无' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">运输方式:</span>
            <span class="info-value">{{ getDictLabel(DELIVERY_TYPE, delivery.deliveryType) || '暂无' }}</span>
          </div>
        </div>

        <div class="delivery-footer">
          <div class="delivery-amount">
            <span class="amount-label">应收款:</span>
            <span class="amount-value">¥{{ delivery.totalFee || '0.00' }}</span>
          </div>
          <div class="delivery-actions">
            <div class="action-btn detail-btn" @click.stop="goToDeliveryDetail(delivery.id)">
              查看详情
            </div>
            <div class="action-btn deliver-btn" @click.stop="confirmDelivery(delivery.id)" v-if="delivery.status === 'TO_BE_DELIVER'">
              确认送达
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="empty-state" v-else-if="!loading">
      <van-empty description="暂无送货任务" />
    </div>

    <div class="load-more" v-if="hasMore && deliveries.length > 0">
      <van-loading v-if="loading" type="spinner" size="24px">加载中...</van-loading>
      <div v-else @click="loadMore" class="load-more-text">加载更多</div>
    </div>
  </div>
</template>

<script>
import { Search, Tag, Empty, Loading, Toast, Dialog } from 'vant'
import { getBillList } from '@/api/bill'
import { getUserInfo } from '@/api/user'
import { GOODS_NAME, BILL_STATUS, DELIVERY_TYPE, getDictLabel, getDictTagType } from '@/utils/dict'

export default {
  name: 'DeliveryList',
  components: {
    [Search.name]: Search,
    [Tag.name]: Tag,
    [Empty.name]: Empty,
    [Loading.name]: Loading
  },
  data() {
    return {
      searchValue: '',
      loading: false,
      hasMore: true,
      pageNum: 1,
      pageSize: 10,
      total: 0,
      deliveries: [],
      userId: null,
      GOODS_NAME,
      BILL_STATUS,
      DELIVERY_TYPE
    }
  },
  computed: {
    totalAmount() {
      return this.deliveries.reduce((total, delivery) => {
        const fee = parseFloat(delivery.totalFee || 0)
        return total + (isNaN(fee) ? 0 : fee)
      }, 0).toFixed(2)
    }
  },
  async created() {
    await this.getUserId()
    this.fetchDeliveries()
  },
  async activated() {
    // keep-alive 缓存的组件在激活时重新获取数据
    // 清空搜索值，确保每次进入页面都是全新的数据
    this.searchValue = ''
    this.pageNum = 1
    
    // 确保有用户ID
    if (!this.userId) {
      await this.getUserId()
    }
    
    // 使用 nextTick 确保页面先渲染完成，再请求数据，避免阻塞页面切换
    this.$nextTick(() => {
      this.fetchDeliveries()
    })
  },
  methods: {
    getDictLabel,
    getDictTagType,
    async getUserId() {
      try {
        const res = await getUserInfo()
        if (res.code === 200 && res.user) {
          this.userId = res.user.userId
        }
      } catch (error) {
        console.error('获取用户信息失败', error)
        Toast('获取用户信息失败')
      }
    },
    async fetchDeliveries(isLoadMore = false) {
      if (!this.userId) {
        console.warn('用户ID未获取到，无法查询送货列表')
        return
      }
      
      try {
        this.loading = true
        
        const params = {
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          employeeId: this.userId, // 按员工ID查询
          phone: this.searchValue || undefined
        }
        
        const res = await getBillList(params)
        
        if (res.code === 200) {
          const { list, total } = res.data
          
          if (isLoadMore) {
            this.deliveries = [...this.deliveries, ...list]
          } else {
            this.deliveries = list
          }
          
          this.total = total
          this.hasMore = this.deliveries.length < total
        } else {
          Toast(res.msg || '获取送货列表失败')
        }
      } catch (error) {
        console.error('获取送货列表出错', error)
        Toast('获取送货列表失败')
      } finally {
        this.loading = false
      }
    },
    
    onSearch() {
      this.pageNum = 1
      this.fetchDeliveries()
    },
    
    goToDeliveryDetail(id) {
      this.$router.push(`/billDetail/${id}`)
    },
    
    async confirmDelivery(id) {
      try {
        await Dialog.confirm({
          title: '确认送达',
          message: '确认此订单已成功送达？',
        })
        
        Toast.loading({
          message: '处理中...',
          forbidClick: true,
          duration: 1000,
        })
        
        setTimeout(() => {
          Toast.success('送达确认成功')
          this.fetchDeliveries() // 刷新列表
        }, 1000)
        
      } catch (error) {
        // 用户取消
      }
    },
    
    loadMore() {
      if (this.loading || !this.hasMore) return
      
      this.pageNum++
      this.fetchDeliveries(true)
    }
  }
}
</script>

<style scoped>
.delivery-list-container {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-bottom: 50px;
}

.header {
  background-color: #ffffff;
  padding: 10px 0;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.title {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin: 10px 0;
  color: #333;
}

.stats-bar {
  display: flex;
  padding: 10px 15px;
  background-color: #fff7e6;
  margin: 0 15px 10px;
  border-radius: 8px;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
  color: #ff8800;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

.search-box {
  padding: 0 15px;
}

.delivery-list {
  padding: 10px 15px;
}

.delivery-item {
  background-color: #ffffff;
  border-radius: 12px;
  margin-bottom: 15px;
  padding: 15px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s;
}

.delivery-item:active {
  transform: scale(0.98);
}

.delivery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.order-info {
  display: flex;
  flex-direction: column;
}

.order-type {
  margin-bottom: 5px;
}

.order-no {
  font-size: 12px;
  color: #999;
}

.delivery-status {
  font-size: 14px;
  font-weight: bold;
}

.delivery-info {
  margin-bottom: 15px;
  background-color: #fafafa;
  padding: 10px;
  border-radius: 8px;
}

.info-row {
  display: flex;
  margin: 5px 0;
  font-size: 14px;
}

.info-label {
  min-width: 80px;
  color: #666;
  font-weight: 500;
}

.info-value {
  flex: 1;
  color: #333;
}

.delivery-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.delivery-amount {
  padding: 5px 0;
}

.amount-label {
  font-size: 14px;
  color: #666;
}

.amount-value {
  font-size: 18px;
  font-weight: bold;
  color: #f44;
  margin-left: 5px;
}

.delivery-actions {
  display: flex;
}

.action-btn {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  margin-left: 10px;
  font-weight: 500;
  cursor: pointer;
}

.detail-btn {
  border: 1px solid #ddd;
  color: #666;
}

.deliver-btn {
  background-color: #ff8800;
  color: #fff;
  box-shadow: 0 2px 6px rgba(255, 136, 0, 0.3);
}

.empty-state {
  padding: 40px 0;
}

.load-more {
  text-align: center;
  padding: 15px 0;
  color: #999;
}

.load-more-text {
  font-size: 14px;
}
</style> 